import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cpu, Youtube, Radar, Factory, Send, BarChart3, ShieldCheck, Zap, LogIn } from "lucide-react";
import api, { TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const formatDetail = (d) => {
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
    return "No se pudo iniciar sesión. Intenta de nuevo.";
};

function PasswordForm() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.session_token) localStorage.setItem(TOKEN_KEY, data.session_token);
            setUser(data.user);
            navigate("/", { replace: true, state: { user: data.user } });
        } catch (err) {
            setError(
                err.response
                    ? formatDetail(err.response.data?.detail)
                    : "Sin conexión con el servidor. Revisa tu internet e intenta de nuevo."
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="mt-6 max-w-md border border-border bg-secondary/40 p-4" data-testid="password-login-form">
            <div className="mb-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                O entra con tu correo y contraseña
            </div>
            <input
                data-testid="login-email-input"
                type="email"
                required
                autoComplete="email"
                placeholder="correo@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mb-2 w-full border border-border bg-black px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary"
            />
            <input
                data-testid="login-password-input"
                type="password"
                required
                autoComplete="current-password"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mb-3 w-full border border-border bg-black px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary"
            />
            {error && (
                <div data-testid="login-error" className="mb-3 border border-destructive/60 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    {error}
                </div>
            )}
            <button
                data-testid="password-login-btn"
                type="submit"
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2 border border-primary px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-black disabled:opacity-50"
            >
                <LogIn size={15} /> {busy ? "Entrando…" : "Entrar"}
            </button>
        </form>
    );
}

const FEATURES = [
    { icon: Radar, title: "Investigación de nichos", desc: "IA que detecta los nichos más rentables y virales." },
    { icon: Factory, title: "Producción automática", desc: "Guion, voz, subtítulos y video renderizados solos." },
    { icon: Send, title: "Publicación en YouTube", desc: "Subida real con SEO, comentarios y auto-respuestas." },
    { icon: BarChart3, title: "Rendimiento real", desc: "Métricas de YouTube en vivo con dictamen por color." },
];

export default function Login() {
    return (
        <div className="grain min-h-screen bg-background text-foreground">
            <div className="mx-auto flex min-h-screen max-w-6xl flex-col px-5 py-8 md:px-8">
                {/* header */}
                <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center border border-primary text-primary">
                        <Cpu size={18} />
                    </div>
                    <div>
                        <div className="font-head text-base leading-none">AUTOTUBE</div>
                        <div className="text-[10px] tracking-[0.3em] text-muted-foreground">ORCHESTRATOR</div>
                    </div>
                </div>

                {/* hero */}
                <div className="grid flex-1 items-center gap-10 py-10 md:grid-cols-2 md:py-16">
                    <div>
                        <div className="mb-4 inline-flex items-center gap-2 border border-border bg-secondary px-3 py-1 text-[10px] uppercase tracking-widest text-primary">
                            <Zap size={12} /> Fábrica autónoma de canales
                        </div>
                        <h1 className="font-head text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
                            Tu ecosistema de YouTube en{" "}
                            <span className="text-primary">piloto automático</span>.
                        </h1>
                        <p className="mt-5 max-w-md text-sm text-muted-foreground md:text-base">
                            Investiga nichos, crea canales, produce videos con IA y publícalos en
                            YouTube. Todo con TUS propias claves e integraciones — aislado y privado
                            para tu cuenta.
                        </p>
                        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                            <button
                                data-testid="google-login-btn"
                                onClick={startLogin}
                                className="group inline-flex items-center justify-center gap-3 border border-primary bg-primary px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-black transition-all duration-200 hover:bg-transparent hover:text-primary"
                            >
                                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                                    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.2 35.3 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
                                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.3C41.4 36.4 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/>
                                </svg>
                                Entrar con Google
                            </button>
                            <span className="flex items-center gap-1.5 text-[11px] tracking-wider text-muted-foreground">
                                <ShieldCheck size={13} className="text-primary" /> Datos e integraciones aislados por cuenta
                            </span>
                        </div>
                        <PasswordForm />
                    </div>

                    <div className="relative">
                        <div className="grid grid-cols-2 gap-3">
                            {FEATURES.map((f) => {
                                const Icon = f.icon;
                                return (
                                    <div key={f.title} className="border border-border bg-secondary/40 p-4 transition-colors hover:border-primary/60">
                                        <Icon size={18} className="text-primary" />
                                        <div className="mt-3 font-head text-xs">{f.title}</div>
                                        <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{f.desc}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-3 flex items-center justify-center gap-2 border border-border bg-black py-3 text-[10px] tracking-widest text-muted-foreground">
                            <Youtube size={13} className="text-primary" /> POWERED BY IA · EDGE-TTS · FFMPEG
                        </div>
                    </div>
                </div>

                <div className="border-t border-border pt-4 text-center text-[10px] tracking-widest text-muted-foreground">
                    © AUTOTUBE ORCHESTRATOR · PRODUCTO DIGITAL
                </div>
            </div>
        </div>
    );
}
